









































































































































































































































































import {
  CourseFilterOptions,
  CourseLocationOption,
  CourseStatusOption,
  CourseTypeDTO,
  CourseTypeOption
} from '@/store/modules/jtms/course/course.types';
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import {
  CourseLocation,
  CourseStatus,
  CourseVisibility
} from '@/jbi-shared/types/jtms.types';
import { deepCopy } from '@/jbi-shared/util/deep-copy.util';

@Component({})
export default class CourseSearch extends Vue {
  @Prop(Object) public courseFilterParams!: CourseFilterOptions;
  @Prop(Array) public courseTypes!: CourseTypeDTO[];

  // Configure default option
  selectedStatusOption: CourseStatusOption = {
    id: 0,
    displayName: 'All Status',
    status: undefined,
    visibility: undefined,
    openForRegistration: undefined
  };

  selectedCourseTypeOption: CourseTypeOption = {
    id: 0,
    name: 'All Type'
  };

  selectedLocationOption: CourseLocationOption = {
    id: 0,
    name: 'All Location'
  };

  enableStatusFilter: boolean = true;

  // Statically generated status options for selection
  statusOptions: CourseStatusOption[] = [
    {
      id: 1,
      displayName: 'Published (Public) Open',
      status: CourseStatus.PUBLISHED,
      visibility: CourseVisibility.PUBLIC,
      openForRegistration: 'true'
    },
    {
      id: 2,
      displayName: 'Published (Public) Close',
      status: CourseStatus.PUBLISHED,
      visibility: CourseVisibility.PUBLIC,
      openForRegistration: 'false'
    },
    {
      id: 3,
      displayName: 'Published (Private) Open',
      status: CourseStatus.PUBLISHED,
      visibility: CourseVisibility.PRIVATE,
      openForRegistration: 'true'
    },
    {
      id: 4,
      displayName: 'Published (Private) Closed',
      status: CourseStatus.PUBLISHED,
      visibility: CourseVisibility.PRIVATE,
      openForRegistration: 'false'
    },
    {
      id: 5,
      displayName: 'Draft',
      status: CourseStatus.DRAFT,
      visibility: undefined,
      openForRegistration: undefined
    },
    {
      id: 6,
      displayName: 'Cancelled',
      status: CourseStatus.CANCELLED,
      visibility: undefined,
      openForRegistration: undefined
    },
    {
      id: 7,
      displayName: 'In progress',
      status: CourseStatus.IN_PROGRESS,
      visibility: undefined,
      openForRegistration: undefined
    }
  ];

  locationOptions: CourseLocationOption[] = [
    { id: 1, name: CourseLocation.REMOTE },
    { id: 2, name: CourseLocation.VENUE },
    { id: 3, name: CourseLocation.BOTH }
  ];

  originalStatusOptions: CourseStatusOption[] = [...this.statusOptions];

  get shouldDisableStatusFilter() {
    const courseStatusParam = this.$route.params.courseStatus;

    if (courseStatusParam) {
      // Route has a 'courseStatus' param
      if (courseStatusParam !== 'published') {
        // If it's not 'published', disable the filter
        this.enableStatusFilter = false;
      } else {
        // If it's 'published', enable the filter, but modify options
        this.enableStatusFilter = true;
        // Splice out the last three options
        this.statusOptions = this.statusOptions.slice(0, 4);
      }
    } else {
      // No 'courseStatus' param, enable filter with all options
      this.enableStatusFilter = true;
      // Reset status options to the full list (if modified previously)
      this.statusOptions = [...this.originalStatusOptions];
    }

    return this.enableStatusFilter;
  }

  get getTypeOptions() {
    const types = Object.values(this.courseTypes).map(
      (courseType: CourseTypeDTO) => ({
        id: courseType.id,
        slug: courseType.name,
        name: courseType.name
      })
    );
    return [{ id: 0, name: 'All Type', slug: '' }, ...types];
  }

  get getLocationOptions() {
    const locations = this.locationOptions.map((location) => ({
      id: location.id,
      slug: location.name,
      name: location.name
    }));
    return [{ id: 0, name: 'All Location', slug: '' }, ...locations];
  }

  get getStatusOptions() {
    const courseStatusParam = this.$route.params.courseStatus;
    if (courseStatusParam === 'published') {
      return [
        {
          id: 0,
          displayName: 'All Status',
          status: CourseStatus.PUBLISHED,
          visibility: undefined,
          openForRegistration: undefined
        },
        ...this.statusOptions
      ];
    }

    return [
      {
        id: 0,
        displayName: 'All Status',
        status: undefined,
        visibility: undefined,
        openForRegistration: undefined
      },
      ...this.statusOptions
    ];
  }

  isPublishedOption(status: string): boolean {
    return status === CourseStatus.PUBLISHED;
  }

  getStatusDisplayName(status: string): string {
    switch (status) {
      case 'DRAFT':
        return 'Draft';
      case 'IN_PROGRESS':
        return 'In progress';
      case 'CANCELLED':
        return 'Cancelled';
      case 'PUBLISHED':
        return 'Published';
      default:
        return 'All Status';
    }
  }

  getVisibilityDisplayName(courseVisibility: string): string {
    switch (courseVisibility) {
      case 'PRIVATE':
        return 'Private';
      case 'PUBLIC':
        return 'Public';
      default:
        return '';
    }
  }

  public resetFilter() {
    return this.$emit('resetFilter');
  }

  @Watch('courseFilterParams', { deep: true })
  @Debounce(600)
  public onChange() {
    this.$emit('input', this.courseFilterParams);
  }

  @Watch('selectedStatusOption')
  onSelectedStatusOptionChange(newOption: CourseStatusOption | null) {
    if (newOption) {
      this.courseFilterParams.status = newOption.status;
      this.courseFilterParams.visibility = newOption.visibility;
      this.courseFilterParams.openForRegistration =
        newOption.openForRegistration;
    } else {
      this.courseFilterParams.status = '';
      this.courseFilterParams.visibility = '';
      this.courseFilterParams.openForRegistration = '';
    }
  }

  @Watch('selectedCourseTypeOption')
  onSelectedCourseTypeChange() {
    if (this.selectedCourseTypeOption.name === 'All Type') {
      this.courseFilterParams.type = '';
      return;
    }

    this.courseFilterParams.type = this.selectedCourseTypeOption.name;
  }

  @Watch('selectedLocationOption')
  onSelectedLocationChange() {
    if (this.selectedLocationOption.name === 'All Location') {
      this.courseFilterParams.location = '';
      return;
    }

    this.courseFilterParams.location = this.selectedLocationOption.name;
  }

  @Watch('courseFilterParams.type')
  onTypeFieldChanged() {
    if (this.courseFilterParams.type === '') {
      this.selectedCourseTypeOption = this.getTypeOptions[0];
    }
  }

  @Watch('courseFilterParams.status')
  @Watch('courseFilterParams.openForRegistration')
  @Watch('courseFilterParams.visibility')
  onStatusFieldChanged() {
    if (this.courseFilterParams.status === '') {
      this.selectedStatusOption = this.getStatusOptions[0];
    }

    if (
      this.courseFilterParams.status === CourseStatus.PUBLISHED &&
      this.courseFilterParams.openForRegistration === '' &&
      this.courseFilterParams.visibility === ''
    ) {
      this.selectedStatusOption = this.getStatusOptions[0];
    }
  }

  @Watch('courseFilterParams.location')
  onLocationFieldChanged() {
    if (this.courseFilterParams.location === '') {
      this.selectedLocationOption = this.getLocationOptions[0];
    }
  }
}
